import { uniqueId } from 'lodash-es';
import React, { createContext, useCallback, useContext, useState } from 'react';

import Button from '../stories/Button/Button';
import { ModalPanel } from '../stories/ModalPanel/ModalPanel';

interface ModalError {
    id: string;
    content: React.ReactNode;
    action?: {
        label: string;
        onClick(modal: ModalError): void;
    }
}

interface ModalErrorContextProps {
    addError(error: Omit<ModalError, 'id'>): void
    removeError(id: string): void
}

const ModalErrorContext = createContext({} as ModalErrorContextProps);

export function ModalErrorProvider({ children }: React.PropsWithChildren) {
    const [errors, setErrors] = useState<ModalError[]>([]);

    const addError = useCallback((error: Omit<ModalError, 'id'>) => {
        setErrors(errors => errors.concat({
            id: uniqueId('modalError_'),
            ...error,
        }));
    }, []);

    const removeError = useCallback((id: string) => {
        setErrors(errors => errors.filter(err => err.id !== id));
    }, []);

    const modal = errors[errors.length - 1] as ModalError | void;

    return (
        <ModalErrorContext.Provider value={{ addError, removeError }}>
            {children}
            {!!modal && (
                <ModalPanel
                    isOpen
                    key={modal.id}
                    id={modal.id}
                    className="watch-modal--dynamic"
                    onRequestClose={() => removeError(modal.id)}
                    shouldCloseOnOverlayClick
                >
                    <div className='watch-modal__content text-center'>
                        {modal.content}
                    </div>

                    {modal.action && (
                        <div className='watch-modal__footer'>
                            <div className='watch-modal__footer__button-container pb-4'>
                                <Button buttonClass="sticky-button"
                                    label={modal.action.label}
                                    action={() => modal.action?.onClick(modal)}
                                />
                                <Button buttonClass="sticky-button sticky-button--transparent"
                                    label="Close"
                                    action={() => removeError(modal.id)}
                                />
                            </div>
                        </div>
                    )}
                </ModalPanel>
            )}
        </ModalErrorContext.Provider>
    );
}

export function useModalError() {
    return useContext(ModalErrorContext);
}
